import { formatDate } from "@/lib/utils";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { PageContainer } from "@/components/layout/PageContainer";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  PlusCircle,
  Shield,
  Edit,
  Trash2,
  CheckCircle2,
  MoreVertical,
  AlertTriangle,
} from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../../api/apiClient";
import { API_ENDPOINTS } from "../../api/header";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Spinner } from "@/components/ui/spinner";
import { setuserpermission_data } from "../../core/redux/action";
import { all_routes } from "../../Router/all_routes";
const getClearPermissionName = (perm, groupDescription) => {
  let baseName = perm.name.replace(/([a-z])([A-Z])/g, "$1 $2");

  const valueWords = perm.value.toLowerCase().split(/[\s_-]+/);
  const groupWords = (groupDescription || "").toLowerCase().split(/[\s_-]+/);
  const nameWords = baseName.toLowerCase().split(/[\s_-]+/);

  // Get all words in value except the base name (e.g. 'pos', 'payment' from 'pos payment create')
  const modifierWords = valueWords.filter(
    (w) => !nameWords.includes(w) && w !== "",
  );

  // Check if there are any words in modifierWords that are NOT in the group description
  const hasExternalModifiers = modifierWords.some(
    (w) => !groupWords.includes(w),
  );

  if (hasExternalModifiers) {
    // If there are external modifiers, use the full modifier phrase to avoid ambiguity
    // e.g., 'pos payment' instead of just 'pos'
    const prefix = modifierWords
      .map((w) => {
        if (w === "pos") return "POS";
        return w.charAt(0).toUpperCase() + w.slice(1);
      })
      .join(" ");
    return `${prefix} ${baseName}`;
  }

  return baseName;
};
export function RolesPage() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.user_data);
  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };
  useEffect(() => {
    if (!hasPermission("role read")) {
      navigate(all_routes.dashboard);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 15,
  });
  const [rowSelection, setRowSelection] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    description: "",
    status: 1,
    permissions: [],
  });
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);
  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = all_routes.signin;
  };
  // Queries
  const { data: rolesData, isLoading: isLoadingRoles } = useQuery({
    queryKey: ["roles", pagination.pageIndex, pagination.pageSize, searchText],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.USERS.ROLES, {
        params: {
          page: pagination.pageIndex + 1,
          per_page: pagination.pageSize,
          search: searchText,
        },
      });
      return res.data;
    },
    enabled: hasPermission("role read"),
  });
  const roles = rolesData?.data || [];
  const totalCount =
    rolesData?.total ?? rolesData?.meta?.total ?? roles.length ?? 0;
  const pageCount = rolesData?.last_page ?? rolesData?.meta?.last_page ?? 1;
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [searchText]);
  const { data: permissionsList = [] } = useQuery({
    queryKey: ["permissions_list"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.USERS.PERMISSIONS);
      return res.data.permissions || [];
    },
    enabled: hasPermission("role create") || hasPermission("role update"),
  });
  // Mutations
  const saveRoleMutation = useMutation({
    mutationFn: async (data) => {
      if (data.id) {
        return apiClient.put(`${API_ENDPOINTS.USERS.ROLES}/${data.id}`, data);
      }
      return apiClient.post(API_ENDPOINTS.USERS.ROLES, data);
    },
    onSuccess: () => {
      toast.success(`Role ${isEdit ? "updated" : "added"} successfully!`);
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      setIsModalOpen(false);
      refreshUserPermissions();
      if (isEdit && currentUser?.roles?.some((r) => r.id === formData.id)) {
        setShowLogoutPopup(true);
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Something went wrong!");
    },
  });
  const deleteRoleMutation = useMutation({
    mutationFn: async (id) => {
      return apiClient.delete(`${API_ENDPOINTS.USERS.ROLES}/${id}`);
    },
    onSuccess: () => {
      toast.success("Role deleted successfully!");
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      setDeleteDialogOpen(false);
      refreshUserPermissions();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Something went wrong!");
    },
  });
  const refreshUserPermissions = async () => {
    try {
      const res = await apiClient.get(API_ENDPOINTS.AUTH.ME);
      if (res.data?.data?.permissions) {
        dispatch(setuserpermission_data(res.data.data.permissions));
      }
    } catch (error) {
      console.error("Failed to refresh permissions", error);
    }
  };
  const handleOpenModal = async (role = null) => {
    if (role) {
      setIsEdit(true);
      try {
        const res = await apiClient.get(
          `${API_ENDPOINTS.USERS.ROLES}/${role.id}`,
        );
        const roleData = res.data.data;
        setFormData({
          id: roleData.id,
          name: roleData.name,
          description: roleData.description || "",
          status: roleData.status,
          permissions: roleData.permissions?.map((p) => p.value) || [],
        });
      } catch {
        toast.error("Failed to fetch role details");
        return;
      }
    } else {
      setIsEdit(false);
      setFormData({
        id: null,
        name: "",
        description: "",
        status: 1,
        permissions: [],
      });
    }
    setIsModalOpen(true);
  };
  const handlePermissionChange = (value) => {
    setFormData((prev) => {
      const isSelected = prev.permissions.includes(value);
      if (isSelected) {
        return {
          ...prev,
          permissions: prev.permissions.filter((p) => p !== value),
        };
      }
      return {
        ...prev,
        permissions: [...prev.permissions, value],
      };
    });
  };
  const isGroupFullySelected = (groupPermissions) => {
    return groupPermissions.every((p) =>
      formData.permissions.includes(p.value),
    );
  };
  const handleSelectAllGroup = (groupPermissions) => {
    const groupValues = groupPermissions.map((p) => p.value);
    const fullySelected = isGroupFullySelected(groupPermissions);
    setFormData((prev) => {
      if (fullySelected) {
        return {
          ...prev,
          permissions: prev.permissions.filter((p) => !groupValues.includes(p)),
        };
      }
      return {
        ...prev,
        permissions: Array.from(new Set([...prev.permissions, ...groupValues])),
      };
    });
  };
  const getMissingDependencies = () => {
    const missing = [];
    if (!permissionsList) return missing;
    const allPermissions = permissionsList.flatMap((group) =>
      group.permissions.map((p) => ({
        ...p,
        groupDescription: group.description,
      })),
    );
    const permissionNameMap = new Map(
      allPermissions.map((p) => [p.value, `${p.groupDescription} ${p.name}`]),
    );
    formData.permissions.forEach((permValue) => {
      const permObj = allPermissions.find((p) => p.value === permValue);
      if (permObj && permObj.dependencies && permObj.dependencies.length > 0) {
        const unmet = permObj.dependencies.filter(
          (dep) => !formData.permissions.includes(dep),
        );
        if (unmet.length > 0) {
          missing.push({
            permissionValue: permValue,
            permissionName: permObj.name,
            unmetDependenciesNames: unmet.map(
              (depValue) => permissionNameMap.get(depValue) || depValue,
            ),
          });
        }
      }
    });
    return missing;
  };
  const missingDependencies = getMissingDependencies();
  const columns = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "name",
      header: "Role Name",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <div className="bg-primary/10 p-2 rounded-full text-primary">
            <Shield className="h-4 w-4" />
          </div>
          <div className="font-semibold text-foreground">
            {row.getValue("name")}
          </div>
        </div>
      ),
    },
    {
      accessorKey: "created_at",
      header: "Date Created",
      cell: ({ row }) => (
        <div className="text-sm text-muted-foreground font-medium">
          {formatDate(row.getValue("created_at"))}
        </div>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <Badge
          variant={row.getValue("status") === 1 ? "success" : "destructive"}
          className="capitalize px-3 py-0.5"
        >
          {row.getValue("status") === 1 ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const role = row.original;
        if (role.is_owner)
          return (
            <Badge
              variant="outline"
              className="text-[10px] uppercase tracking-wider opacity-50"
            >
              System Protected
            </Badge>
          );
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {hasPermission("role update") && (
                <DropdownMenuItem onClick={() => handleOpenModal(role)}>
                  <Edit className="mr-2 h-4 w-4" /> Edit Role
                </DropdownMenuItem>
              )}
              {hasPermission("role delete") && (
                <DropdownMenuItem
                  className="text-red-600 focus:text-red-600"
                  onClick={() => {
                    setRoleToDelete(role);
                    setDeleteDialogOpen(true);
                  }}
                >
                  <Trash2 className="mr-2 h-4 w-4" /> Delete Role
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];
  return (
    <PageContainer>
      <PageHeader className="animate-in fade-in slide-in-from-top-4 duration-500">
        <PageHeaderHeading
          title="Roles & Permissions"
          subtitle="Manage system roles and define granular access permissions for users."
        />
        <PageHeaderActions>
          {hasPermission("role create") && (
            <Button onClick={() => handleOpenModal()} className="h-10 px-4">
              <PlusCircle className="mr-2 h-4 w-4" />
              Add New Role
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>
      <FilterCard className="animate-in fade-in slide-in-from-top-4 duration-500 delay-75">
        <FilterSearch
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search roles by name..."
        />
      </FilterCard>
      <TableCard className="animate-in fade-in slide-in-from-bottom-8 duration-700 delay-150">
        <DataTable
          columns={columns}
          data={roles}
          isLoading={isLoadingRoles}
          onRowClick={(role) =>
            !role.is_owner &&
            hasPermission("role update") &&
            handleOpenModal(role)
          }
          pagination={pagination}
          onPaginationChange={setPagination}
          pageCount={pageCount}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          getRowId={(row) => row.id}
          footerInfo={
            <div className="flex items-center gap-4">
              <span className="font-medium text-foreground">
                Total Roles: {totalCount}
              </span>
            </div>
          }
        />
      </TableCard>
      {/* Role Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-hidden flex flex-col gap-0 border shadow-2xl bg-background">
          <DialogHeader className="px-8 py-6 border-b shrink-0 bg-muted/20">
            <DialogTitle className="text-2xl font-bold tracking-tight text-foreground">
              {isEdit ? "Edit Role & Permissions" : "Create New Role"}
            </DialogTitle>
            <p className="text-muted-foreground text-sm mt-1">
              {isEdit
                ? "Modify the permissions and details for this existing role."
                : "Define a new role and configure its access levels."}
            </p>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            <div className="p-8 space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 bg-muted/20 p-6 rounded-xl border border-border">
                <div className="space-y-3">
                  <Label
                    htmlFor="role-name"
                    className="text-sm font-semibold text-foreground"
                  >
                    Role Name
                  </Label>
                  <Input
                    id="role-name"
                    placeholder="e.g. Sales Manager"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="h-11 bg-background"
                  />
                  <p className="text-[11px] text-muted-foreground italic">
                    Use a descriptive name for the role.
                  </p>
                </div>
                <div className="space-y-3">
                  <Label
                    htmlFor="role-status"
                    className="text-sm font-semibold text-foreground"
                  >
                    Status
                  </Label>
                  <Select
                    value={String(formData.status)}
                    onValueChange={(val) =>
                      setFormData({ ...formData, status: Number(val) })
                    }
                  >
                    <SelectTrigger
                      id="role-status"
                      className="h-11 bg-background"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Active</SelectItem>
                      <SelectItem value="0">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground italic">
                    Inactive roles cannot be assigned to users.
                  </p>
                </div>
              </div>
              <div className="space-y-6">
                <div className="flex items-center justify-between border-b border-border pb-3">
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-primary" />
                    <Label className="text-xl font-bold text-foreground">
                      Permission Settings
                    </Label>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="select-all-permissions"
                        checked={
                          permissionsList.length > 0 &&
                          permissionsList.every((group) =>
                            group.permissions.every((p) =>
                              formData.permissions.includes(p.value),
                            ),
                          )
                        }
                        onCheckedChange={(checked) => {
                          if (checked) {
                            const allPermissions = permissionsList.flatMap(
                              (group) => group.permissions.map((p) => p.value),
                            );
                            setFormData((prev) => ({
                              ...prev,
                              permissions: allPermissions,
                            }));
                          } else {
                            setFormData((prev) => ({
                              ...prev,
                              permissions: [],
                            }));
                          }
                        }}
                      />
                      <Label
                        htmlFor="select-all-permissions"
                        className="text-sm font-medium cursor-pointer"
                      >
                        Select All
                      </Label>
                    </div>
                    <Badge
                      variant="outline"
                      className="text-xs px-3 py-1 bg-primary/5"
                    >
                      {formData.permissions.length} Permissions Selected
                    </Badge>
                  </div>
                </div>
                <div className="grid gap-6">
                  {permissionsList.map((group) => {
                    const groupMissing = missingDependencies.filter((m) =>
                      group.permissions.some(
                        (p) => p.value === m.permissionValue,
                      ),
                    );
                    return (
                      <div
                        key={group.name}
                        className="group border border-border rounded-xl bg-background/50 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300"
                      >
                        <div className="bg-muted/30 px-5 py-4 flex items-center justify-between border-b border-border transition-colors group-hover:bg-muted/40">
                          <div className="flex items-center space-x-3">
                            <Checkbox
                              id={`group-${group.name}`}
                              checked={isGroupFullySelected(group.permissions)}
                              onCheckedChange={() =>
                                handleSelectAllGroup(group.permissions)
                              }
                              className="h-5 w-5"
                            />
                            <Label
                              htmlFor={`group-${group.name}`}
                              className="text-base font-bold cursor-pointer text-foreground"
                            >
                              {group.description}
                            </Label>
                          </div>
                          <Badge
                            variant="secondary"
                            className="bg-background text-[10px] font-bold py-0.5"
                          >
                            {group.permissions.length} modules
                          </Badge>
                        </div>
                        <div className="p-5">
                          {groupMissing.length > 0 && (
                            <div className="mb-4 text-sm text-amber-600 bg-amber-500/10 p-3 rounded-lg border border-amber-500/20 flex gap-3">
                              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                              <div>
                                <p className="font-semibold mb-1">
                                  Missing dependencies for this module:
                                </p>
                                <ul className="list-disc pl-4 space-y-1 text-amber-600/90">
                                  {groupMissing.map((m) => (
                                    <li key={m.permissionValue}>
                                      <strong>{m.permissionName}</strong>{" "}
                                      requires{" "}
                                      <span className="font-semibold">
                                        {m.unmetDependenciesNames.join(", ")}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          )}
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {group.permissions.map((perm) => (
                              <div
                                key={perm.value}
                                className="flex items-center space-x-3 p-2 rounded-lg hover:bg-muted/20 transition-colors"
                              >
                                <Checkbox
                                  id={perm.value}
                                  checked={formData.permissions.includes(
                                    perm.value,
                                  )}
                                  onCheckedChange={() =>
                                    handlePermissionChange(perm.value)
                                  }
                                  className="h-4 w-4"
                                />
                                <Label
                                  htmlFor={perm.value}
                                  className="text-sm font-medium cursor-pointer leading-tight text-muted-foreground hover:text-foreground transition-colors"
                                >
                                  {getClearPermissionName(
                                    perm,
                                    group.description,
                                  )}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="px-8 py-6 bg-muted/40 border-t border-border shrink-0 flex items-center justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setIsModalOpen(false)}
              className="px-6 h-11 border-border bg-background hover:bg-muted font-semibold"
            >
              Cancel
            </Button>
            <Button
              onClick={() => saveRoleMutation.mutate(formData)}
              disabled={saveRoleMutation.isPending || !formData.name}
              className="px-8 h-11 font-bold min-w-[140px]"
            >
              {saveRoleMutation.isPending ? (
                <div className="flex items-center gap-2">
                  <Spinner className="h-4 w-4" />
                  <span>Saving...</span>
                </div>
              ) : (
                "Save Role"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the role "{roleToDelete?.name}". This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteRoleMutation.mutate(roleToDelete.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteRoleMutation.isPending ? (
                <Spinner className="mr-2" />
              ) : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={showLogoutPopup} onOpenChange={setShowLogoutPopup}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Role Settings Updated</AlertDialogTitle>
            <AlertDialogDescription>
              Role settings have been successfully updated. You must log out and
              log in again for the changes to take effect.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={handleLogout}>
              Log Out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
